"use client";

import Link from "next/link";
import { useEffect, useRef, type ReactNode } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, X, type LucideIcon } from "lucide-react";
import { useLang } from "@/contexts/language-context";
import AnimatedSection, { AnimatedElement } from "@/components/marketing/AnimatedSection";

/**
 * Bloques compartidos por las landings de captación (/partners, /agentes).
 *
 * Mismo lenguaje visual que la home, /capital y las feature pages: glows de
 * fondo, titulares `font-black` con una palabra en gradiente, tarjetas blancas
 * con sombra, bloques oscuros `#111` con brillo, pasos "01 / 02 / 03" y FAQ en
 * tarjetas. Una idea por bloque, poco texto, un solo destino: `#formulario`.
 */

/** Los anuncios en español llevan ?l=es; si el visitante tiene la cookie en
 *  inglés, se cambia UNA vez (mismo truco que /diagnostico). */
export function useForcedLang(forcedLang: "es" | "en" | null) {
  const { lang, toggleLang } = useLang();
  const toggled = useRef(false);
  useEffect(() => {
    if (forcedLang && forcedLang !== lang && !toggled.current) {
      toggled.current = true;
      toggleLang();
    }
  }, [forcedLang, lang, toggleLang]);
}

const GRADIENT_TEXT = {
  background: "linear-gradient(135deg,#D7263D,#F4722B)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  backgroundClip: "text",
} as const;

/* ─────────────────────────── Botones ─────────────────────────── */

export function CtaButton({
  label,
  href = "#formulario",
  tone = "dark",
}: {
  label: string;
  href?: string;
  tone?: "dark" | "gradient" | "light";
}) {
  const base =
    "group inline-flex items-center justify-center gap-2 px-8 py-4 text-[15px] font-bold rounded-full transition-all active:scale-95 whitespace-nowrap cursor-pointer";
  const tones = {
    dark: "bg-redy-charcoal text-white shadow-lg hover:bg-black",
    gradient: "text-white shadow-lg shadow-redy-orange/25 hover:scale-[1.03]",
    light: "bg-white text-redy-charcoal border border-black/10 shadow-sm hover:bg-[#F0EDE8]",
  };
  return (
    <a
      href={href}
      className={`${base} ${tones[tone]}`}
      style={tone === "gradient" ? { background: "linear-gradient(90deg,#D7263D,#F4722B)" } : undefined}
    >
      {label}
      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
    </a>
  );
}

/* ─────────────────────────── Hero ─────────────────────────── */

export function LandingHero({
  eyebrow,
  title,
  accent,
  lead,
  cta,
  secondary,
  trust,
  visual,
  children,
}: {
  eyebrow: string;
  /** Titular; `accent` es la frase final que va en gradiente. */
  title: string;
  accent: string;
  lead: string;
  cta: string;
  secondary?: { label: string; href: string };
  trust: { icon: LucideIcon; label: string }[];
  visual: ReactNode;
  /** Lo que va encima del eyebrow (p. ej. el selector de puerta). */
  children?: ReactNode;
}) {
  return (
    <section className="relative pt-32 md:pt-36 pb-20 px-6 overflow-hidden bg-[#FAFAFA]">
      <div className="absolute top-0 right-0 w-[700px] h-[700px] bg-redy-orange/6 rounded-full blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-redy-red/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="relative z-10"
        >
          {children}

          <div className="inline-flex items-center gap-2 bg-white border border-black/5 px-4 py-2 rounded-full text-xs font-bold text-redy-charcoal mb-8 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-redy-orange animate-pulse" />
            {eyebrow}
          </div>

          <h1 className="text-[38px] sm:text-5xl md:text-[64px] font-black leading-[1.02] tracking-tight mb-6 text-redy-charcoal">
            {title} <span className="redy-text-gradient">{accent}</span>
          </h1>

          <p className="text-lg md:text-xl text-redy-secondary max-w-[540px] leading-relaxed mb-9 font-medium">
            {lead}
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <CtaButton label={cta} />
            {secondary ? (
              <a
                href={secondary.href}
                className="inline-flex items-center justify-center text-[15px] font-bold px-8 py-4 rounded-full border border-black/10 bg-white hover:bg-[#F0EDE8] text-redy-charcoal shadow-sm transition-all"
              >
                {secondary.label}
              </a>
            ) : null}
          </div>

          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3">
            {trust.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 text-[13px] font-bold text-redy-secondary">
                <Icon className="w-4 h-4 text-redy-orange shrink-0" />
                {label}
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative flex items-center justify-center"
        >
          <div className="absolute w-[600px] h-[460px] bg-redy-orange/12 rounded-full blur-[120px] -z-10" />
          {visual}
        </motion.div>
      </div>
    </section>
  );
}

/** Tarjeta oscura del hero, como el contador de /capital: fondo #141414, línea
 *  de gradiente arriba, sombra profunda. */
export function HeroCard({ kicker, children }: { kicker: string; children: ReactNode }) {
  return (
    <div className="relative w-full max-w-[360px]">
      <div
        className="relative rounded-[24px] border border-white/8 overflow-hidden shadow-[0_40px_100px_rgba(0,0,0,0.32)]"
        style={{ background: "#141414" }}
      >
        <div className="h-[3px]" style={{ background: "linear-gradient(90deg,#D7263D,#F4722B)" }} />
        <div className="p-7">
          <p className="text-[10px] font-bold tracking-[0.22em] uppercase text-redy-orange">{kicker}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

/** Tarjeta blanca flotante al lado del visual del hero (solo en xl, donde cabe
 *  fuera de la tarjeta oscura sin tapar su contenido). */
export function FloatingCard({
  className = "",
  delay = 0.9,
  from = "left",
  children,
}: {
  className?: string;
  delay?: number;
  from?: "left" | "right" | "top";
  children: ReactNode;
}) {
  const initial = from === "left" ? { x: -20, opacity: 0 } : from === "right" ? { x: 20, opacity: 0 } : { y: -20, opacity: 0 };
  return (
    <motion.div
      initial={initial}
      animate={{ x: 0, y: 0, opacity: 1 }}
      transition={{ delay }}
      className={`absolute z-10 bg-white rounded-[20px] shadow-xl border border-black/5 px-4 py-3 hidden xl:flex items-center gap-3 whitespace-nowrap ${className}`}
    >
      {children}
    </motion.div>
  );
}

/** Línea de un "timeline" dentro de la HeroCard. */
export function TimelineRow({
  done,
  title,
  sub,
  last = false,
}: {
  done: boolean;
  title: string;
  sub: string;
  last?: boolean;
}) {
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <span
          className={`w-5 h-5 rounded-full grid place-items-center shrink-0 ${done ? "" : "border border-white/20"}`}
          style={done ? { background: "linear-gradient(135deg,#D7263D,#F4722B)" } : undefined}
        >
          {done ? <Check size={11} strokeWidth={3.5} color="#fff" /> : <span className="w-1.5 h-1.5 rounded-full bg-redy-orange animate-pulse" />}
        </span>
        {!last ? <span className="w-px flex-1 bg-white/10 my-1" /> : null}
      </div>
      <div className={last ? "" : "pb-4"}>
        <p className="text-[13px] font-bold text-white leading-tight">{title}</p>
        <p className="text-[11.5px] text-white/45 leading-snug mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────── Banda de datos ─────────────────────────── */

export function Band({ items }: { items: { value: string; label: string; sub: string }[] }) {
  return (
    <div className="bg-white py-14 border-y border-black/5">
      <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-2 gap-y-8 md:gap-y-0 md:grid-cols-4 md:divide-x md:divide-black/5">
        {items.map((s, i) => (
          <AnimatedElement key={s.label} delay={0.08 * i} className="text-center px-4 py-2">
            <div className="text-[40px] md:text-[52px] font-black tracking-tight leading-none mb-2" style={GRADIENT_TEXT}>
              {s.value}
            </div>
            <div className="text-[14px] font-black text-redy-charcoal mb-1">{s.label}</div>
            <div className="text-[12px] font-medium text-redy-secondary">{s.sub}</div>
          </AnimatedElement>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────── Secciones ─────────────────────────── */

export function Section({
  id,
  tone = "white",
  children,
  className = "",
}: {
  id?: string;
  tone?: "white" | "soft";
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className={`relative py-24 px-6 scroll-mt-24 ${tone === "soft" ? "bg-[#FAFAFA]" : "bg-white"} ${className}`}>
      <div className="max-w-[1200px] mx-auto">{children}</div>
    </section>
  );
}

export function SectionHead({
  eyebrow,
  title,
  muted,
  lead,
  center = false,
}: {
  eyebrow: string;
  title: string;
  /** Segunda línea del titular, en gris claro. */
  muted?: string;
  lead?: string;
  center?: boolean;
}) {
  return (
    <AnimatedSection className={`mb-14 ${center ? "text-center max-w-[720px] mx-auto" : "max-w-[660px]"}`}>
      <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">{eyebrow}</div>
      <h2 className="text-4xl md:text-[52px] font-black tracking-tight leading-[1.05] text-redy-charcoal">
        {title}
        {muted ? (
          <>
            <br />
            <span className="text-black/30">{muted}</span>
          </>
        ) : null}
      </h2>
      {lead ? <p className="text-lg text-redy-secondary mt-5 leading-relaxed font-medium">{lead}</p> : null}
    </AnimatedSection>
  );
}

/** Pasos "01 / 02 / 03" con número en gradiente, como /capital. */
export function StepsGrid({ items }: { items: { title: string; body: string }[] }) {
  return (
    <div className="grid md:grid-cols-3 gap-8 md:gap-10">
      {items.map((s, i) => (
        <AnimatedElement key={s.title} delay={i * 0.1}>
          <div className="flex items-baseline gap-3 mb-4">
            <span className="text-[40px] font-black leading-none" style={GRADIENT_TEXT}>
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="flex-1 h-px bg-black/8" />
          </div>
          <h3 className="text-[21px] font-black text-redy-charcoal leading-snug">{s.title}</h3>
          <p className="text-[15px] text-redy-secondary leading-relaxed mt-2.5">{s.body}</p>
        </AnimatedElement>
      ))}
    </div>
  );
}

/** Tarjetas blancas con icono, una idea cada una. */
export function BenefitCards({ items }: { items: { icon: LucideIcon; title: string; body: string }[] }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
      {items.map(({ icon: Icon, title, body }, i) => (
        <AnimatedElement
          key={title}
          delay={0.08 * i}
          className="bg-white rounded-[24px] border border-black/5 p-6 shadow-sm hover:shadow-xl hover:-translate-y-0.5 transition-all"
        >
          <div className="w-12 h-12 rounded-[16px] bg-redy-orange/10 flex items-center justify-center mb-5">
            <Icon className="w-[22px] h-[22px] text-redy-orange" />
          </div>
          <h3 className="text-[17px] font-black text-redy-charcoal leading-snug mb-2">{title}</h3>
          <p className="text-[14px] text-redy-secondary leading-relaxed">{body}</p>
        </AnimatedElement>
      ))}
    </div>
  );
}

/** Dos columnas: lo que sí / lo que no, como "Sin letra chica" de /capital. */
export function YesNo({
  yesTitle,
  yes,
  noTitle,
  no,
}: {
  yesTitle: string;
  yes: string[];
  noTitle: string;
  no: string[];
}) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <AnimatedElement delay={0} className="rounded-[24px] border border-black/[0.07] p-7 md:p-8" style={{ background: "#F7F5F1" }}>
        <h3 className="text-[13px] font-black tracking-[0.14em] uppercase text-redy-charcoal mb-6">{yesTitle}</h3>
        <ul className="space-y-4">
          {yes.map((line) => (
            <li key={line} className="flex gap-3">
              <span className="shrink-0 w-5 h-5 rounded-full grid place-items-center mt-0.5" style={{ background: "rgba(46,204,113,0.14)" }} aria-hidden>
                <Check size={12} strokeWidth={3.5} color="#2D7A3A" />
              </span>
              <span className="text-[15px] text-redy-charcoal leading-relaxed">{line}</span>
            </li>
          ))}
        </ul>
      </AnimatedElement>
      <AnimatedElement delay={0.1} className="rounded-[24px] border border-black/[0.07] p-7 md:p-8 bg-white">
        <h3 className="text-[13px] font-black tracking-[0.14em] uppercase text-redy-charcoal mb-6">{noTitle}</h3>
        <ul className="space-y-4">
          {no.map((line) => (
            <li key={line} className="flex gap-3">
              <span className="shrink-0 w-5 h-5 rounded-full grid place-items-center mt-0.5" style={{ background: "rgba(0,0,0,0.05)" }} aria-hidden>
                <X size={12} strokeWidth={3.5} color="#9B9690" />
              </span>
              <span className="text-[15px] text-redy-secondary leading-relaxed">{line}</span>
            </li>
          ))}
        </ul>
      </AnimatedElement>
    </div>
  );
}

/** Aviso visible, no escondido (misma caja que la divulgación de /capital). */
export function NoticeBox({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-8 rounded-2xl border border-black/[0.07] p-6 md:p-7" style={{ background: "#FCFAF6" }}>
      <p className="text-[13.5px] text-redy-secondary leading-relaxed max-w-[860px]">
        <span className="font-bold text-redy-charcoal">{title}</span> {children}
      </p>
    </div>
  );
}

/* ─────────────────────────── FAQ ─────────────────────────── */

export function FaqGrid({ items }: { items: { q: string; a: string }[] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-left max-w-[1000px] mx-auto">
      {items.map((f, i) => (
        <AnimatedElement key={f.q} delay={0.08 * i} className="bg-[#FAFAFA] border border-black/5 rounded-[32px] p-8 hover:shadow-lg transition-all group">
          <h3 className="text-[16px] font-bold text-redy-charcoal mb-3 pr-6 group-hover:text-redy-orange transition-colors">{f.q}</h3>
          <p className="text-[14px] font-medium text-redy-secondary leading-relaxed">{f.a}</p>
        </AnimatedElement>
      ))}
    </div>
  );
}

/* ─────────────────────────── Fundador ─────────────────────────── */

export function FounderSection({
  photo,
  eyebrow,
  quote,
  extraLine,
}: {
  photo: string | null;
  eyebrow: string;
  quote: string;
  extraLine?: string;
}) {
  const { t } = useLang();
  return (
    <section className="px-6 py-6 bg-white">
      <div className="max-w-[1200px] mx-auto">
        <AnimatedSection className="relative bg-[#111] rounded-[40px] p-10 md:p-16 overflow-hidden text-white">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-redy-orange/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-redy-red/10 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/3 pointer-events-none" />
          <div
            className="absolute inset-0 opacity-[0.04] pointer-events-none"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <div className="relative grid md:grid-cols-[auto_1fr] gap-10 md:gap-14 items-center">
            <div className="flex justify-center md:justify-start">
              <div className="relative">
                <div className="absolute inset-0 m-auto w-[220px] h-[220px] bg-redy-orange/25 rounded-full blur-[50px]" />
                {photo ? (
                  // eslint-disable-next-line @next/next/no-img-element -- foto estática en /public, sin optimización remota
                  <img
                    src={photo}
                    alt="Samuel Gómez"
                    className="relative w-[200px] h-[200px] rounded-full object-cover border-4 border-white/10 shadow-2xl"
                  />
                ) : (
                  <div
                    className="relative w-[200px] h-[200px] rounded-full border-4 border-white/10 shadow-2xl grid place-items-center text-white text-5xl font-black"
                    style={{ background: "linear-gradient(135deg,#D7263D,#F4722B)" }}
                  >
                    SG
                  </div>
                )}
              </div>
            </div>

            <div>
              <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-5">{eyebrow}</div>
              <p className="text-2xl md:text-[34px] font-black tracking-tight leading-[1.15]">“{quote}”</p>
              {extraLine ? <p className="text-[17px] md:text-lg text-white/70 font-medium mt-5 leading-relaxed">{extraLine}</p> : null}
              <div className="mt-8 flex items-center gap-4">
                <span className="h-px w-10 bg-white/20" />
                <div>
                  <div className="text-[15px] font-black">Samuel Gómez</div>
                  <div className="text-[13px] text-white/50 font-medium">
                    {t("Founder of Redy · Owner, Empanada Factory (Dallas)", "Fundador de Redy · Dueño de Empanada Factory (Dallas)")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}

/* ─────────────────────────── Formulario ─────────────────────────── */

export function FormSection({
  eyebrow,
  title,
  lead,
  points,
  children,
  footnote,
}: {
  eyebrow: string;
  title: string;
  lead: string;
  /** Lo que se habla en la llamada, tres líneas. */
  points: string[];
  children: ReactNode;
  footnote: ReactNode;
}) {
  const { t } = useLang();
  return (
    <section id="formulario" className="relative py-24 px-6 bg-[#FAFAFA] scroll-mt-20 overflow-hidden">
      <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-redy-orange/6 rounded-full blur-[140px] pointer-events-none" />
      <div className="relative max-w-[1200px] mx-auto grid lg:grid-cols-[1fr_1.15fr] gap-12 lg:gap-16 items-start">
        <AnimatedSection className="lg:sticky lg:top-28">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">{eyebrow}</div>
          <h2 className="text-4xl md:text-[52px] font-black tracking-tight leading-[1.05] text-redy-charcoal">{title}</h2>
          <p className="text-lg text-redy-secondary mt-5 leading-relaxed font-medium">{lead}</p>

          <div className="mt-8 inline-flex items-center gap-2 bg-white border border-black/5 px-4 py-2 rounded-full text-xs font-bold text-redy-charcoal shadow-sm">
            <span className="w-2 h-2 rounded-full bg-redy-green" />
            {t("15 minutes · no commitment", "15 minutos · sin compromiso")}
          </div>

          <ul className="mt-8 space-y-4">
            {points.map((p) => (
              <li key={p} className="flex gap-3">
                <span className="shrink-0 w-6 h-6 rounded-full grid place-items-center mt-0.5" style={{ background: "linear-gradient(135deg,#D7263D,#F4722B)" }} aria-hidden>
                  <Check size={12} strokeWidth={3.5} color="#fff" />
                </span>
                <span className="text-[15px] text-redy-charcoal leading-relaxed font-medium">{p}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8">{footnote}</div>
        </AnimatedSection>

        <AnimatedElement delay={0.1} className="bg-white rounded-[32px] border border-black/5 shadow-[0_32px_80px_rgba(0,0,0,0.10)] p-2 sm:p-4">
          {children}
        </AnimatedElement>
      </div>
    </section>
  );
}

/** Nota del formulario: SMS con consentimiento, y qué es (y qué no es) lo que sigue. */
export function FormFootnote({ children }: { children: ReactNode }) {
  return (
    <p className="text-[13px] text-redy-secondary leading-relaxed max-w-xl">
      {children}{" "}
      <Link href="/sms-terms" className="underline underline-offset-2 hover:text-redy-charcoal">
        SMS
      </Link>
      .
    </p>
  );
}
